import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import NoticeDetailModal from 'components/modal/NoticeDetailModal';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { restfetcher } from 'queryClient';

interface NoticeCardProps {
  title: string;
  attachments?: any;
  contents: string;
  boardidx: number;
  submenuidx: number;
  boardSearchList: BoardSearchList[];
  currentIndex: number;
  updatedate: string;
}

const NoticeCard = ({
  title,
  contents,
  boardidx,
  submenuidx,
  attachments,
  boardSearchList,
  currentIndex
}: NoticeCardProps) => {
  const [openNoticeDetailModal, setOpenNoticeDetailModal] = useState(false);

  const increaseViewCountApi = useMutation({
    mutationFn: (param: number) =>
      restfetcher({
        method: 'POST',
        path: 'biz/board/increase/view',
        params: {
          boardidx: param
        }
      })
  });

  const handleClickOpenModal = (boardidx: number) => {
    setOpenNoticeDetailModal(true);

    if (boardidx !== 0) {
      increaseViewCountApi.mutate(boardidx);
    }
  };

  const handleClickCloseModal = () => {
    setOpenNoticeDetailModal(false);
  };

  const labels = (submenuidx: number) => {
    switch (submenuidx) {
      case 41:
        return {
          color: '#FE7C04',
          text: '공지'
        };
      case 42:
        return {
          color: '#53D63E',
          text: '업데이트'
        };
      case 43:
        return {
          color: '#9E9E9E',
          text: '휴일안내'
        };
      default:
        return {
          color: '#FE7C04',
          text: '공지'
        };
    }
  };

  const labelInfo = labels(submenuidx);

  const removeEmptyParagraphs = (contents: string) => {
    return contents.replace(/<p>(&nbsp;|\s)*<\/p>/gi, '');
  };

  return (
    <>
      <Wrapper
        key={boardidx}
        $hasImages={attachments.length > 0}
        onClick={() => handleClickOpenModal(boardidx)}
      >
        <Label $labelColor={labelInfo.color}>{labelInfo.text}</Label>
        <h3 className="innerTitle">{title}</h3>
        <ContentsWrapper $hasImages={attachments.length > 0} className="wrapper">
          {contents && (
            <p
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(removeEmptyParagraphs(contents))
              }}
            />
          )}
        </ContentsWrapper>
      </Wrapper>
      {openNoticeDetailModal && (
        <NoticeDetailModal
          open={openNoticeDetailModal}
          onCancel={handleClickCloseModal}
          boardSearchList={boardSearchList}
          currentIndex={currentIndex}
        />
      )}
    </>
  );
};

export default NoticeCard;

const Wrapper = styled.div<{ $hasImages: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 435px;
  height: 280px;
  padding: 32px;
  border-radius: 12px;
  border: 1.5px solid ${color.gray200};
  background: ${color.white};
  cursor: pointer;
  overflow: hidden;

  &:hover {
    box-shadow: 0px 0px 16px 0px ${color.opacityBlack02};
    transition: all 0.5s;
  }

  & > h3.innerTitle {
    width: 100%;
    color: ${color.black2};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow-x: clip;
    ${font.h4_medium};
  }
`;

const Label = styled.div<{ $labelColor: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  width: fit-content;
  padding: 4px 12px;
  border-radius: 4px;
  background: ${({ $labelColor }) => $labelColor};
  color: ${color.white2};
  text-align: center;
  ${font.caption2_medium};
`;

const ContentsWrapper = styled.div<{ $hasImages: boolean }>`
  color: ${color.gray600};
  ${font.caption1_regular};
  overflow: hidden;

  ${({ $hasImages }) => ($hasImages ? `-webkit-line-clamp: 3;` : `-webkit-line-clamp: 7;`)}

  & figure.image {
    display: none;
  }
`;
